import { useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { FileText } from 'lucide-react'
import { PdfPreview } from '@/components/pdf-preview'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/use-auth'
import { cn } from '@/lib/utils'
import { getApiErrorMessage } from '@/lib/api-error'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:80'

/** The PO's stored PDF, rendered in place of the page so staff can read it without downloading. */
export function POPdfView({
  poUuid,
  poNumber,
  className = 'mx-auto max-w-4xl',
  viewportClassName = 'max-h-[calc(100dvh-12rem)]',
}: {
  poUuid: string
  poNumber: string
  className?: string
  viewportClassName?: string
}) {
  const { getAccessToken } = useAuth()
  const pdf = useQuery({
    queryKey: ['purchase-orders', 'pdf', poUuid],
    queryFn: async ({ signal }) => {
      const token = await getAccessToken()
      if (!token) throw new Error('Not authenticated')
      const response = await fetch(`${API_BASE_URL}/dashboard/purchase-orders/${poUuid}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        signal,
      })
      if (!response.ok) throw new Error('The PO PDF could not be loaded')
      return response.blob()
    },
    // The PDF is regenerated when the PO changes, so each visit fetches the current copy.
    gcTime: 0,
    refetchOnWindowFocus: false,
    retry: false,
  })
  const url = useMemo(() => (pdf.data ? URL.createObjectURL(pdf.data) : null), [pdf.data])
  useEffect(
    () => () => {
      if (url) URL.revokeObjectURL(url)
    },
    [url],
  )

  if (pdf.isError) {
    return (
      <div
        className="flex min-h-80 flex-col items-center justify-center gap-3 rounded-xl border px-6 text-center"
        role="alert"
      >
        <FileText aria-hidden="true" className="size-8 text-muted-foreground" />
        <p className="text-sm font-semibold">{getApiErrorMessage(pdf.error, 'The PDF could not be loaded')}</p>
        <Button onClick={() => void pdf.refetch()} size="sm" variant="outline">
          Try again
        </Button>
      </div>
    )
  }
  if (!url) {
    return (
      <div
        aria-label="Loading PO PDF"
        className={cn('min-h-[40rem] animate-pulse rounded-xl bg-muted/30', className)}
        role="status"
      />
    )
  }
  return (
    <div className={className}>
      <PdfPreview
        key={url}
        name={`PO #${poNumber}`}
        url={url}
        viewportClassName={viewportClassName}
      />
    </div>
  )
}
